import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { Select } from 'primeng/select';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Project } from '../../models/project.model';
import { ProjectService } from '../../services/project.service';
import { MilestonePlanTask, MilestonePlanTaskService } from '../../services/milestone-plan-task.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';

/** Milestone status for filtering and badges */
type MilestoneStatusFilter = 'onTrack' | 'atRisk' | 'overdue';

interface DeliveryRowBase {
  projectId: string;
  projectLabel: string;
  projectCode: string;
  manager: string;
  client: string;
  serviceGroup: string;
  milestoneStatus: MilestoneStatusFilter;
}

interface UpcomingMilestoneRow extends DeliveryRowBase {
  milestone: string;
  dueDateLabel: string;
  dueDateTs: number;
}

interface PlannedActualRow extends DeliveryRowBase {
  plannedPct: number;
  actualPct: number;
  variancePct: number;
}

/** Stacked horizontal timeline: months of each segment type (sum = 12 for Jan–Dec). */
interface TimelineProject extends DeliveryRowBase {
  completedMonths: number;
  plannedMonths: number;
  behindMonths: number;
}

interface SelectOption {
  label: string;
  value: string | null;
}

@Component({
  selector: 'app-project-health-delivery',
  standalone: true,
  imports: [CommonModule, FormsModule, TableModule, Select],
  templateUrl: './project-health-delivery.component.html',
  styleUrl: './project-health-delivery.component.scss'
})
export class ProjectHealthDeliveryComponent implements OnInit, OnDestroy {
  private static readonly INITIAL_TIMELINE_LIMIT = 10;
  private readonly initialTimelineStatus: MilestoneStatusFilter = 'overdue';
  private hasAppliedInitialChartFilter = false;
  readonly timelineYear = new Date().getFullYear();
  readonly monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  selectedManager: string | null = null;
  selectedClient: string | null = null;
  selectedServiceGroup: string | null = null;
  selectedStatus: string | null = null;

  managerOptions: SelectOption[] = [{ label: 'All', value: null }];
  clientOptions: SelectOption[] = [{ label: 'All', value: null }];
  serviceGroupOptions: SelectOption[] = [{ label: 'All', value: null }];
  statusOptions: SelectOption[] = [
    { label: 'All', value: null },
    { label: 'On track', value: 'onTrack' },
    { label: 'At risk', value: 'atRisk' },
    { label: 'Overdue', value: 'overdue' }
  ];

  milestonesThisMonth = 0;
  milestonesThisMonthSub = '';
  completedOnTime = 0;
  completedOnTimeSub = '';
  atRiskCount = 0;
  atRiskSub = 'Action needed';
  overdueCount = 0;
  overdueSub = 'Escalation required';

  upcomingRows: UpcomingMilestoneRow[] = [];
  completionRows: PlannedActualRow[] = [];
  visibleTimelineRows: TimelineProject[] = [];

  timelineChartData: { labels: string[]; datasets: unknown[] } = { labels: [], datasets: [] };
  timelineChartOptions: Record<string, unknown> = {};
  loading = true;

  private masterUpcoming: UpcomingMilestoneRow[] = [];
  private masterCompletion: PlannedActualRow[] = [];
  private masterTimeline: TimelineProject[] = [];
  private milestonesThisMonthCountByProjectId = new Map<string, number>();

  constructor(
    private projectService: ProjectService,
    private milestoneTaskService: MilestonePlanTaskService,
    public pageHeaderService: PageHeaderSearchActionService
  ) {}

  ngOnInit(): void {
    // Enable the header funnel icon so the filter bar collapses/expands like
    // every other page (29 of them use this pattern).
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.initChartOptions();
    this.loadDashboardData();
  }

  ngOnDestroy(): void {
    this.pageHeaderService.setCanShowFilterToggle(false);
  }

  private loadDashboardData(): void {
    this.loading = true;
    this.projectService.getAllProjects().pipe(
      catchError(() => of([] as Project[]))
    ).subscribe(projects => {
      const activeProjects = projects.filter(project => project.isActive);
      if (activeProjects.length === 0) {
        this.masterUpcoming = [];
        this.masterCompletion = [];
        this.masterTimeline = [];
        this.hasAppliedInitialChartFilter = false;
        this.buildFilterOptions();
        this.applyFilters();
        this.loading = false;
        return;
      }

      const taskRequests = activeProjects.map(project =>
        this.milestoneTaskService.getByProjectId(project.id).pipe(
          catchError(() => of([] as MilestonePlanTask[]))
        )
      );

      forkJoin(taskRequests).subscribe(taskLists => {
        this.milestonesThisMonthCountByProjectId = this.buildMilestonesThisMonthCountMap(activeProjects, taskLists);
        this.masterUpcoming = this.buildUpcomingRows(activeProjects, taskLists);
        this.masterCompletion = this.buildCompletionRows(activeProjects, taskLists);
        this.masterTimeline = this.buildTimelineRows(activeProjects, taskLists);
        this.hasAppliedInitialChartFilter = false;
        this.buildFilterOptions();
        this.applyFilters();
        this.loading = false;
      });
    });
  }


  onFilterChange(): void {
    this.applyFilters();
  }

  private buildFilterOptions(): void {
    const managers = [...new Set([...this.masterUpcoming, ...this.masterCompletion, ...this.masterTimeline].map(r => r.manager))].sort();
    const clients = [...new Set([...this.masterUpcoming, ...this.masterCompletion, ...this.masterTimeline].map(r => r.client))].sort();
    const groups = [...new Set([...this.masterUpcoming, ...this.masterCompletion, ...this.masterTimeline].map(r => r.serviceGroup))].sort();
    this.managerOptions = [{ label: 'All', value: null }, ...managers.map(m => ({ label: m, value: m }))];
    this.clientOptions = [{ label: 'All', value: null }, ...clients.map(c => ({ label: c, value: c }))];
    this.serviceGroupOptions = [{ label: 'All', value: null }, ...groups.map(g => ({ label: g, value: g }))];
  }

  private applyFilters(): void {
    const pass = (row: DeliveryRowBase): boolean => {
      if (this.selectedManager && row.manager !== this.selectedManager) {
        return false;
      }
      if (this.selectedClient && row.client !== this.selectedClient) {
        return false;
      }
      if (this.selectedServiceGroup && row.serviceGroup !== this.selectedServiceGroup) {
        return false;
      }
      if (this.selectedStatus && row.milestoneStatus !== this.selectedStatus) {
        return false;
      }
      return true;
    };

    this.upcomingRows = this.masterUpcoming.filter(pass);
    this.completionRows = this.masterCompletion.filter(pass);

    const filteredTimeline = this.masterTimeline.filter(pass);
    let timelineForDisplay = filteredTimeline;
    if (!this.hasActiveFilters() && !this.hasAppliedInitialChartFilter) {
      timelineForDisplay = this.getInitialTimelineRows(filteredTimeline);
      this.hasAppliedInitialChartFilter = true;
    }

    if (!this.hasVisibleRows(timelineForDisplay)) {
      const fallbackRows = this.getInitialTopTimelineRows(filteredTimeline);
      timelineForDisplay = this.hasVisibleRows(fallbackRows) ? fallbackRows : filteredTimeline;
    }

    this.visibleTimelineRows = timelineForDisplay;
    this.rebuildTimelineChart(timelineForDisplay);
    this.recomputeKpis(timelineForDisplay);
  }

  // Public: the filter-bar clear button binds to this in the template.
  hasActiveFilters(): boolean {
    return !!(this.selectedManager || this.selectedClient || this.selectedServiceGroup || this.selectedStatus);
  }

  clearFilters(): void {
    this.selectedManager = null;
    this.selectedClient = null;
    this.selectedServiceGroup = null;
    this.selectedStatus = null;
    this.onFilterChange();
  }

  private getInitialTopTimelineRows(rows: TimelineProject[]): TimelineProject[] {
    const sorted = [...rows]
      .sort((a, b) => {
        const aMilestones = this.milestonesThisMonthCountByProjectId.get(a.projectId) ?? 0;
        const bMilestones = this.milestonesThisMonthCountByProjectId.get(b.projectId) ?? 0;
        if (bMilestones !== aMilestones) {
          return bMilestones - aMilestones;
        }
        const aTotal = a.completedMonths + a.plannedMonths + a.behindMonths;
        const bTotal = b.completedMonths + b.plannedMonths + b.behindMonths;
        return bTotal - aTotal;
      });

    const overdue = sorted.filter(row => row.milestoneStatus === 'overdue');
    if (overdue.length >= ProjectHealthDeliveryComponent.INITIAL_TIMELINE_LIMIT) {
      return overdue.slice(0, ProjectHealthDeliveryComponent.INITIAL_TIMELINE_LIMIT);
    }

    const nonOverdue = sorted.filter(row => row.milestoneStatus !== 'overdue');
    return [...overdue, ...nonOverdue].slice(0, ProjectHealthDeliveryComponent.INITIAL_TIMELINE_LIMIT);
  }

  private getInitialTimelineRows(rows: TimelineProject[]): TimelineProject[] {
    const initialStatusRows = rows.filter(
      row => row.milestoneStatus === this.initialTimelineStatus && this.hasVisibleTimelineSegments(row)
    );
    if (initialStatusRows.length > 0) {
      return initialStatusRows.slice(0, ProjectHealthDeliveryComponent.INITIAL_TIMELINE_LIMIT);
    }

    const visibleRows = rows.filter(row => this.hasVisibleTimelineSegments(row));
    return this.getInitialTopTimelineRows(visibleRows.length > 0 ? visibleRows : rows);
  }

  private hasVisibleTimelineSegments(row: TimelineProject): boolean {
    return row.completedMonths > 0 || row.plannedMonths > 0 || row.behindMonths > 0;
  }

  private hasVisibleRows(rows: TimelineProject[]): boolean {
    return rows.some(row => this.hasVisibleTimelineSegments(row));
  }

  private recomputeKpis(timeline: TimelineProject[]): void {
    const codes = new Set<string>();
    for (const r of this.upcomingRows) {
      codes.add(r.projectCode);
    }
    for (const r of this.completionRows) {
      codes.add(r.projectCode);
    }
    for (const r of timeline) {
      codes.add(r.projectCode);
    }
    const projectCount = codes.size;

    const onTrack = timeline.filter(t => t.milestoneStatus === 'onTrack').length;
    const atRisk = timeline.filter(t => t.milestoneStatus === 'atRisk').length;
    const overdue = timeline.filter(t => t.milestoneStatus === 'overdue').length;
    const denom = onTrack + atRisk + overdue;

    this.milestonesThisMonth =
      timeline.length === 0
        ? 0
        : Math.max(this.upcomingRows.length, Math.round(timeline.length * 1.6));
    this.milestonesThisMonthSub =
      projectCount > 0 ? `Across ${projectCount} project${projectCount === 1 ? '' : 's'}` : 'No projects in view';

    this.completedOnTime = onTrack;
    this.completedOnTimeSub = denom > 0 ? `${Math.round((onTrack / denom) * 100)}% on-time rate` : '—';
    this.atRiskCount = atRisk;
    this.atRiskSub = atRisk > 0 ? 'Action needed' : 'No action required';
    this.overdueCount = overdue;
    this.overdueSub = overdue > 0 ? 'Escalation required' : 'No escalation';
  }

  private rebuildTimelineChart(rows: TimelineProject[]): void {
    this.timelineChartData = {
      labels: rows.map(r => r.projectLabel),
      datasets: [
        {
          label: 'Completed',
          data: rows.map(r => r.completedMonths),
          backgroundColor: '#22c55e',
          borderRadius: 0,
          borderSkipped: false
        },
        {
          label: 'Planned',
          data: rows.map(r => r.plannedMonths),
          backgroundColor: '#7dd3fc',
          borderRadius: 0,
          borderSkipped: false
        },
        {
          label: 'Behind',
          data: rows.map(r => r.behindMonths),
          backgroundColor: '#ef4444',
          borderRadius: 0,
          borderSkipped: false
        }
      ]
    };
  }

  private initChartOptions(): void {
    this.timelineChartOptions = {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          align: 'start',
          labels: {
            usePointStyle: true,
            pointStyle: 'rect',
            boxWidth: 10,
            padding: 16
          }
        },
        tooltip: {
          callbacks: {
            label: (ctx: { dataset?: { label?: string }; parsed?: { x: number } }) => {
              const v = ctx.parsed?.x ?? 0;
              const months = Math.round(v * 10) / 10;
              return `${ctx.dataset?.label ?? ''}: ${months} mo.`;
            }
          }
        }
      },
      scales: {
        x: {
          stacked: true,
          min: 0,
          max: 12,
          grid: { color: 'rgba(148, 163, 184, 0.25)' },
          ticks: {
            stepSize: 1,
            callback: (value: string | number) => {
              const n = typeof value === 'number' ? value : parseFloat(value);
              if (Number.isInteger(n) && n >= 0 && n < this.monthLabels.length) {
                return this.monthLabels[n];
              }
              return '';
            }
          },
          title: { display: false }
        },
        y: {
          stacked: true,
          grid: { display: false },
          ticks: { font: { size: 11 } }
        }
      }
    };
  }

  statusBadgeClass(status: MilestoneStatusFilter): string {
    switch (status) {
      case 'onTrack':
        return 'status-pill status-pill--success';
      case 'atRisk':
        return 'status-pill status-pill--warning';
      case 'overdue':
        return 'status-pill status-pill--danger';
      default:
        return 'status-pill';
    }
  }

  statusLabel(status: MilestoneStatusFilter): string {
    switch (status) {
      case 'onTrack':
        return 'On track';
      case 'atRisk':
        return 'At risk';
      case 'overdue':
        return 'Overdue';
      default:
        return status;
    }
  }

  varianceBadgeClass(variancePct: number): string {
    if (variancePct > 0) {
      return 'status-pill status-pill--success';
    }
    if (variancePct < 0) {
      return 'status-pill status-pill--danger';
    }
    return 'status-pill status-pill--success';
  }

  varianceLabel(variancePct: number): string {
    const sign = variancePct > 0 ? '+' : '';
    return `${sign}${variancePct}%`;
  }

  timelineSegmentWidth(value: number): number {
    if (value <= 0) {
      return 0;
    }
    return Math.max(0, Math.min(100, (value / 12) * 100));
  }

  private buildUpcomingRows(projects: Project[], taskLists: MilestonePlanTask[][]): UpcomingMilestoneRow[] {
    const today = this.startOfDay(new Date());
    const thirtyDaysLater = this.startOfDay(new Date(today));
    thirtyDaysLater.setDate(thirtyDaysLater.getDate() + 30);
    const rows: UpcomingMilestoneRow[] = [];

    projects.forEach((project, index) => {
      const tasks = taskLists[index] ?? [];
      for (const task of tasks) {
        const dueDate = this.parseDate(task.targetDate);
        if (!dueDate) {
          continue;
        }
        if (dueDate < today || dueDate > thirtyDaysLater) {
          continue;
        }
        rows.push({
          ...this.projectBase(project, this.getProjectHealth(project, tasks)),
          milestone: task.task || 'Milestone',
          dueDateLabel: this.formatDate(dueDate),
          dueDateTs: dueDate.getTime()
        });
      }
    });

    return rows.sort((a, b) => a.dueDateTs - b.dueDateTs);
  }

  private buildCompletionRows(projects: Project[], taskLists: MilestonePlanTask[][]): PlannedActualRow[] {
    return projects.map((project, index) => {
      const tasks = taskLists[index] ?? [];
      const health = this.getProjectHealth(project, tasks);
      const plannedDays = Math.max(project.plannedEstimateDays ?? 0, 0);
      const actualDays = Math.max(project.actualEffortDays ?? 0, 0);
      const plannedPct = plannedDays > 0 ? 100 : 0;
      const actualPct = plannedDays > 0 ? Math.round((actualDays / plannedDays) * 100) : 0;
      const variancePct = actualPct - plannedPct;

      return {
        ...this.projectBase(project, health),
        plannedPct,
        actualPct,
        variancePct
      };
    });
  }

  private buildTimelineRows(projects: Project[], taskLists: MilestonePlanTask[][]): TimelineProject[] {
    const windowStart = new Date(this.timelineYear, 0, 1);
    const windowEnd = new Date(this.timelineYear + 1, 0, 1);
    const now = new Date();

    return projects.map((project, index) => {
      const tasks = taskLists[index] ?? [];
      const health = this.getProjectHealth(project, tasks);
      const start = this.parseDate(project.startDate) ?? windowStart;
      let end = this.parseDate(project.endDate);
      if (!end || end <= start) {
        const fallbackEnd = new Date(start);
        fallbackEnd.setMonth(fallbackEnd.getMonth() + 1);
        end = fallbackEnd;
      }
      const boundedStart = new Date(Math.max(start.getTime(), windowStart.getTime()));
      const boundedEnd = new Date(Math.min(end.getTime(), windowEnd.getTime()));
      const totalMonths = Math.max(this.diffMonths(boundedStart, boundedEnd), 0);
      const completedMonths = Math.max(this.diffMonths(boundedStart, new Date(Math.min(now.getTime(), boundedEnd.getTime()))), 0);
      const behindMonths = health === 'overdue' ? Math.min(Math.max(totalMonths - completedMonths, 0), totalMonths) : 0;
      let plannedMonths = Math.max(totalMonths - completedMonths - behindMonths, 0);
      if (this.round1(totalMonths) === 0) {
        plannedMonths = 0.5;
      }

      return {
        ...this.projectBase(project, health),
        completedMonths: this.round1(completedMonths),
        plannedMonths: this.round1(plannedMonths),
        behindMonths: this.round1(behindMonths)
      };
    });
  }

  private projectBase(project: Project, milestoneStatus: MilestoneStatusFilter): DeliveryRowBase {
    const code = (project.projectCode || '').trim() || project.id.slice(0, 4).toUpperCase();
    const name = (project.name || 'Project').trim();
    return {
      projectId: project.id,
      projectLabel: `${code} — ${name}`,
      projectCode: code,
      manager: (project.managerName || 'Unassigned').trim(),
      client: (project.clientName || 'Unassigned').trim(),
      serviceGroup: (project.serviceGroupName || 'Unassigned').trim(),
      milestoneStatus
    };
  }

  private buildMilestonesThisMonthCountMap(
    projects: Project[],
    taskLists: MilestonePlanTask[][]
  ): Map<string, number> {
    const now = new Date();
    const targetYear = now.getFullYear();
    const targetMonth = now.getMonth();
    const counts = new Map<string, number>();

    projects.forEach((project, index) => {
      const tasks = taskLists[index] ?? [];
      const count = tasks.reduce((sum, task) => {
        const date = this.parseDate(task.targetDate);
        if (!date) {
          return sum;
        }
        if (date.getFullYear() === targetYear && date.getMonth() === targetMonth) {
          return sum + 1;
        }
        return sum;
      }, 0);
      counts.set(project.id, count);
    });

    return counts;
  }

  private getProjectHealth(project: Project, tasks: MilestonePlanTask[]): MilestoneStatusFilter {
    const status = (project.status || '').toLowerCase();
    if (status.includes('red')) {
      return 'overdue';
    }
    if (status.includes('amber') || status.includes('yellow')) {
      return 'atRisk';
    }

    const today = this.startOfDay(new Date());
    const hasOverdueTask = tasks.some(task => {
      const target = this.parseDate(task.targetDate);
      if (!target) {
        return false;
      }
      const taskStatus = (task.status || '').toLowerCase();
      const isDone = taskStatus.includes('completed');
      return target < today && !isDone;
    });
    if (hasOverdueTask) {
      return 'overdue';
    }

    const hasRiskTask = tasks.some(task => {
      const taskStatus = (task.status || '').toLowerCase();
      return taskStatus.includes('off track') || taskStatus.includes('on hold');
    });
    if (hasRiskTask) {
      return 'atRisk';
    }

    return 'onTrack';
  }

  private parseDate(value?: string): Date | null {
    if (!value) {
      return null;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private formatDate(date: Date): string {
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: '2-digit'
    }).format(date);
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private diffMonths(start: Date, end: Date): number {
    const msPerMonth = 1000 * 60 * 60 * 24 * 30;
    return (end.getTime() - start.getTime()) / msPerMonth;
  }

  private round1(value: number): number {
    return Math.round(value * 10) / 10;
  }
}
